import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Button from "flarum/common/components/Button";
import LoadingIndicator from "flarum/common/components/LoadingIndicator";
import classList from "flarum/common/utils/classList";
import { searchEmoji, resolveEmoji, loadEmojiMap, emojiMapReady } from "../utils/emoji";
/**
 * A searchable emoji picker.
 *
 * The discussion composer's picker is an autocomplete triggered by typing `:`
 * mid-sentence, which is the right interaction for prose but wrong for a field
 * whose entire value is one emoji. Here the field *is* the search box: type to
 * filter, click or press Enter to choose.
 *
 * Stores Unicode rather than a shortcode. A stored `❤️` renders everywhere with no
 * map lookup and survives the extension being disabled, whereas a stored `heart`
 * is meaningless without this table.
 */
export default class EmojiPicker extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "open", false);
    _defineProperty(this, "query", "");
    _defineProperty(this, "highlighted", 0);
    _defineProperty(this, "outsideHandler", void 0);
  }
  oninit(vnode) {
    super.oninit(vnode);

    // Warm the chunk so the grid is populated by the time it is opened.
    loadEmojiMap();
  }
  view() {
    const _this$attrs = this.attrs,
      value = _this$attrs.value,
      disabled = _this$attrs.disabled;
    const selected = resolveEmoji(value);
    const results = this.open ? searchEmoji(this.query) : [];
    return m("div", {
      className: classList("EmojiPicker", {
        "EmojiPicker--open": this.open
      })
    }, m("div", {
      className: "EmojiPicker-control"
    }, m("span", {
      className: "EmojiPicker-preview",
      "aria-hidden": "true"
    }, selected != null ? selected : m("i", {
      className: "far fa-face-smile"
    })), m("input", {
      className: "FormControl EmojiPicker-input",
      type: "text",
      value: this.open ? this.query : selected != null ? selected : "",
      placeholder: app.translator.trans("ramon-chat.forum.emoji_picker.placeholder", {}, true),
      disabled: disabled,
      onfocus: () => this.openPicker(),
      oninput: e => this.onInput(e),
      onkeydown: e => this.onKeyDown(e),
      "aria-expanded": this.open ? "true" : "false",
      "aria-autocomplete": "list"
    }), selected ? m(Button, {
      className: "Button Button--icon Button--link EmojiPicker-clear",
      icon: "fas fa-times",
      title: app.translator.trans("ramon-chat.forum.emoji_picker.clear", {}, true),
      disabled: disabled,
      onclick: () => this.choose(null)
    }) : null), this.open ? m("div", {
      className: "EmojiPicker-dropdown"
    }, !emojiMapReady() && results.length === 0 ? m(LoadingIndicator, {
      display: "inline",
      size: "small"
    }) : results.length === 0 ? m("div", {
      className: "EmojiPicker-empty"
    }, app.translator.trans("ramon-chat.forum.emoji_picker.empty")) : m("div", {
      className: "EmojiPicker-grid"
    }, results.map((item, index) => m("button", {
      key: item.name,
      type: "button",
      className: classList("EmojiPicker-option", {
        "EmojiPicker-option--active": index === this.highlighted
      }),
      title: ":".concat(item.name, ":")
      // mousedown, not click: the input's blur would close the
      // dropdown before a click could land on it.
      ,
      onmousedown: e => {
        e.preventDefault();
        this.choose(item.unicode);
      }
    }, item.unicode)))) : null);
  }
  oncreate(vnode) {
    super.oncreate(vnode);

    // Close on any click outside. Bound on the document because the dropdown can
    // be dismissed by interacting with anything else in the modal.
    this.outsideHandler = e => {
      if (!this.open) return;
      if (this.element.contains(e.target)) return;
      this.open = false;
      m.redraw();
    };
    document.addEventListener("mousedown", this.outsideHandler);
  }
  onremove(vnode) {
    super.onremove(vnode);
    if (this.outsideHandler) {
      document.removeEventListener("mousedown", this.outsideHandler);
    }
  }
  openPicker() {
    this.open = true;
    this.query = "";
    this.highlighted = 0;
    m.redraw();
  }
  onInput(e) {
    this.query = e.target.value;
    this.open = true;
    this.highlighted = 0;
    m.redraw();
  }
  onKeyDown(e) {
    const results = searchEmoji(this.query);
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.highlighted = Math.min(this.highlighted + 1, results.length - 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        this.highlighted = Math.max(this.highlighted - 1, 0);
        break;
      case "Enter":
        {
          var _results$this$highlig;
          e.preventDefault();

          // Enter with an exact shortcode typed and nothing highlighted should still
          // resolve — the user may have pasted `:tada:` and pressed Enter.
          const pick = (_results$this$highlig = results[this.highlighted]) != null ? _results$this$highlig : null;
          this.choose(pick ? pick.unicode : resolveEmoji(this.query));
          break;
        }
      case "Escape":
        e.preventDefault();
        this.open = false;
        break;
      default:
        return;
    }
    m.redraw();
  }
  choose(value) {
    this.open = false;
    this.query = "";
    this.attrs.onchange(value);
    m.redraw();
  }
}
flarum.reg.add('ramon-chat', 'forum/components/EmojiPicker', EmojiPicker);